import React, { useEffect, useRef, useState } from "react";
import { FaFacebookF, FaInstagram, FaTiktok, FaWhatsapp } from "react-icons/fa";
import { FaLocationDot } from "react-icons/fa6";
import { MdEmail } from "react-icons/md";
import { FiDownload, FiX, FiChevronLeft, FiChevronRight, FiZoomIn, FiZoomOut } from "react-icons/fi";
import { Counter } from "counterapi";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/legacy/build/pdf";
import img_logo from "../../assets/logoIntellegnium.webp";
import "./footer.css";
import SmartImage from "../SmartImage";
import TrackedContactLink from "../TrackedContactLink";

// Usa el worker oficial de PDF.js para que el visor funcione igual en Chrome y Firefox.
// Si tu proyecto falla al compilar por esta ruta, instala pdfjs-dist con:
// npm install pdfjs-dist
GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const footerLinks = [
  { label: "Inicio", href: "/" },
  { label: "Portafolio", href: "#portafolio" },
  { label: "Servicios", href: "#servicios" },
  { label: "Nosotros", href: "#nosotros" },
  { label: "Aviso de privacidad", pdf: "/pdf/legal/aviso-de-privacidad.pdf" },
  { label: "Terminos y Condiciones", pdf: "/pdf/legal/terminos-condiciones.pdf" },
  { label: "Política de Cookies", pdf: "/pdf/legal/politica-de-cookies.pdf"}
];

const footerServices = [
  { label: "Mascotas", href: "#servicios" },
  { label: "Branding", href: "#servicios" },
  { label: "Registro de Marca", href: "#servicios" },
  { label: "Botargas", href: "#servicios" },
  { label: "Animación y Video", href: "#servicios" },
  { label: "Contenido para Redes", href: "#servicios" },
];

const footerContacts = [
  {
    label: "WhatsApp",
    icon: <FaWhatsapp />,
    kind: "whatsapp",
  },
  {
    label: "Email",
    icon: <MdEmail />,
    kind: "email",
  },
  {
    label: "Monterrey, N.L, México",
    icon: <FaLocationDot />,
    href: "https://maps.google.com",
    external: true,
  },
];

function PdfCanvasViewer({ pdfUrl }) {
  const canvasRef = useRef(null);
  const pdfDocRef = useRef(null);
  const renderTaskRef = useRef(null);

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.15);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadPdf = async () => {
      setLoading(true);
      setError("");
      setPageNumber(1);
      setNumPages(0);

      try {
        const loadingTask = getDocument({
          url: pdfUrl,
        });

        const pdf = await loadingTask.promise;
        if (cancelled) return;

        pdfDocRef.current = pdf;
        setNumPages(pdf.numPages);
        setLoading(false);
      } catch (err) {
        console.error("Error loading PDF:", err);
        if (!cancelled) {
          setError("No se pudo cargar el PDF.");
          setLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;

      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (_) {}
      }

      if (pdfDocRef.current) {
        try {
          pdfDocRef.current.destroy();
        } catch (_) {}
        pdfDocRef.current = null;
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    const renderPage = async () => {
      const pdf = pdfDocRef.current;
      const canvas = canvasRef.current;

      if (!pdf || !canvas || loading || error) return;

      try {
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
        }

        const page = await pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale });
        const outputScale = window.devicePixelRatio || 1;

        const context = canvas.getContext("2d");
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

        renderTaskRef.current = page.render({
          canvasContext: context,
          viewport,
          transform,
        });

        await renderTaskRef.current.promise;
      } catch (err) {
        if (err?.name !== "RenderingCancelledException") {
          console.error("Error rendering PDF page:", err);
          setError("No se pudo renderizar el PDF.");
        }
      }
    };

    renderPage();
  }, [pageNumber, scale, loading, error]);

  return (
    <div className="pdf-viewer-shell">
      <div className="pdf-viewer-toolbar">
        <button
          type="button"
          className="pdf-tool-btn"
          onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          disabled={pageNumber <= 1 || loading || !!error}
        >
          <FiChevronLeft />
          Anterior
        </button>

        <div className="pdf-page-info">
          Página {numPages ? pageNumber : "-"} / {numPages || "-"}
        </div>

        <button
          type="button"
          className="pdf-tool-btn"
          onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
          disabled={pageNumber >= numPages || loading || !!error}
        >
          Siguiente
          <FiChevronRight />
        </button>

        <button
          type="button"
          className="pdf-tool-btn"
          onClick={() => setScale((s) => Math.max(0.75, +(s - 0.1).toFixed(2)))}
          disabled={loading || !!error}
        >
          <FiZoomOut />
        </button>

        <button
          type="button"
          className="pdf-tool-btn"
          onClick={() => setScale((s) => Math.min(2.0, +(s + 0.1).toFixed(2)))}
          disabled={loading || !!error}
        >
          <FiZoomIn />
        </button>

        <a className="pdf-tool-btn pdf-tool-link" href={pdfUrl} download>
          <FiDownload />
          Descargar
        </a>
      </div>

      <div className="pdf-canvas-wrap">
        {loading && !error ? (
          <div className="pdf-loading">Cargando PDF...</div>
        ) : error ? (
          <div className="pdf-error">{error}</div>
        ) : (
          <canvas ref={canvasRef} className="pdf-canvas" />
        )}
      </div>
    </div>
  );
}

const Foooter = () => {
  const [visits, setVisits] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  useEffect(() => {
    const counter = new Counter({ workspace: "site-visits" });
    let isMounted = true;

    counter
      .up("first-counter-4194")
      .then((result) => {
        if (isMounted) {
          setVisits(result.data.up_count);
        }
      })
      .catch((error) => {
        console.error("Error tracking visits:", error);
        if (isMounted) {
          setVisits(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setModalOpen(false);
        setSelectedDoc(null);
      }
    };

    if (modalOpen) {
      window.addEventListener("keydown", onKeyDown);
    }

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalOpen]);

  const handleInicio = (e) => {
    if (window.location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const openPdfModal = (doc) => {
    setSelectedDoc(doc);
    setModalOpen(true);
  };

  const closePdfModal = () => {
    setModalOpen(false);
    setSelectedDoc(null);
  };

  return (
    <>
      <footer className="site-footer" id="footer-contacto">
        <div className="footer-inner">
          <div className="footer-brand">
            <a
              href="/"
              className="footer-logo"
              onClick={(e) => {
                if (window.location.pathname === "/") {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
            >
              <SmartImage
                src={img_logo}
                alt="Intellegnium Diseñadores"
                className="menu_logoTexto"
                width="1200"
                height="800"
              />
            </a>

            <p className="footer-text">
              Agencia de diseño especializados en diseño de personajes, branding,
              registro de marca, animación y edición de video.
            </p>

            <div className="footer-social">
              <a
                href="https://www.facebook.com/IntellegniumDisenadores"
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
              >
                <FaFacebookF />
              </a>

              <a
                href="https://instagram.com/intellegniumdisenadores"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
              >
                <FaInstagram />
              </a>

              <a
                href="https://www.tiktok.com/@intellegniumdisenadores"
                target="_blank"
                rel="noreferrer"
                aria-label="TikTok"
              >
                <FaTiktok />
              </a>
            </div>
          </div>

          <div className="footer-col">
            <h4>ENLACES</h4>
            <nav className="footer-links">
              {footerLinks.map((link) => {
                if (link.label === "Inicio") {
                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      className="footer-link"
                      onClick={handleInicio}
                    >
                      {link.label}
                    </a>
                  );
                }

                if (link.pdf) {
                  return (
                    <button
                      key={link.label}
                      type="button"
                      className="footer-link footer-link-button"
                      onClick={() => openPdfModal({ title: link.label, pdf: link.pdf })}
                    >
                      {link.label}
                    </button>
                  );
                }

                return (
                  <a key={link.label} href={link.href} className="footer-link">
                    {link.label}
                  </a>
                );
              })}
            </nav>
          </div>

          <div className="footer-col">
            <h4>SERVICIOS</h4>
            <nav className="footer-links">
              {footerServices.map((service) => (
                <a key={service.label} href={service.href} className="footer-link">
                  {service.label}
                </a>
              ))}
            </nav>
          </div>

          <div className="footer-col">
            <h4>CONTÁCTANOS</h4>
            {footerContacts.map((item) => {
              if (item.kind) {
                return (
                  <TrackedContactLink
                    key={item.label}
                    kind={item.kind}
                    className="footer-contact"
                    eventLabel={`footer_${item.kind}`}
                    aria-label={item.label}
                  >
                    <span className="footer-contact-icon">{item.icon}</span>
                    <span className="footer-contact-text">{item.label}</span>
                  </TrackedContactLink>
                );
              }

              return (
                <a
                  key={item.label}
                  href={item.href}
                  className="footer-contact"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="footer-contact-icon">{item.icon}</span>
                  <span className="footer-contact-text">{item.label}</span>
                </a>
              );
            })}
          </div>
        </div>

        <div className="footer-bottom">
          <h4>
            &copy; {new Date().getFullYear()} Intellegnium Diseñadores. Todos los
            derechos reservados.
          </h4>

          <p className="footer-visits">
            <span className="footer-visits-dot" />
            {visits === null ? "cargando visitas..." : `${visits} visitas`}
          </p>
        </div>
      </footer>

      {modalOpen && selectedDoc && (
        <div
          className="pdf-modal-overlay"
          onClick={closePdfModal}
          role="presentation"
        >
          <div
            className="pdf-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pdf-modal-title"
          >
            <div className="pdf-modal-header">
              <h3 id="pdf-modal-title">{selectedDoc.title}</h3>

              <button
                type="button"
                className="pdf-modal-icon-button"
                onClick={closePdfModal}
                aria-label="Cerrar"
              >
                <FiX />
              </button>
            </div>

            <div className="pdf-modal-body">
              <PdfCanvasViewer pdfUrl={selectedDoc.pdf} />
            </div>

            <div className="pdf-modal-footer">
              <button
                type="button"
                className="pdf-modal-close"
                onClick={closePdfModal}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Foooter;
